import { Link } from 'react-router-dom';

export default function LeadEnablementPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <div className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <Link 
            to="/"
            className="inline-flex items-center gap-2 text-white/90 hover:text-white mb-8 transition-colors whitespace-nowrap"
          >
            <i className="ri-arrow-left-line"></i>
            Back to home
          </Link>
          
          <div className="flex items-center gap-4 mb-6">
            <div className="w-16 h-16 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <i className="ri-user-search-line text-3xl"></i>
            </div>
            <h1 className="text-5xl font-bold">Lead Enablement</h1>
          </div>
          
          <p className="text-xl text-white/90 leading-relaxed">
            Identify and prioritize high-value opportunities to accelerate your sales pipeline.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 lg:px-8 py-16">
        <div className="prose prose-lg max-w-none">
          <h2 className="text-3xl font-bold text-slate-900 mb-6">What is Lead Enablement?</h2>
          <p className="text-slate-600 leading-relaxed mb-8">
            Our Lead Enablement service helps you identify, qualify, and prioritize the most promising business opportunities. We provide you with the tools, data, and insights needed to accelerate your sales pipeline and close deals faster. By combining advanced data analytics with strategic guidance, we ensure your sales team focuses on the leads that matter most.
          </p>

          <h2 className="text-3xl font-bold text-slate-900 mb-6">Key Features</h2>
          <div className="space-y-6 mb-12">
            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-search-eye-line text-xl text-indigo-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Lead Identification</h3>
                <p className="text-slate-600 leading-relaxed">
                  Discover high-potential leads that match your ideal customer profile using advanced data mining and market intelligence techniques.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-star-line text-xl text-indigo-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Lead Scoring & Prioritization</h3>
                <p className="text-slate-600 leading-relaxed">
                  Rank and prioritize leads based on their likelihood to convert, potential value, and strategic fit with your business objectives.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-file-list-3-line text-xl text-indigo-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Detailed Lead Profiles</h3>
                <p className="text-slate-600 leading-relaxed">
                  Access comprehensive profiles including company information, decision-maker contacts, pain points, and buying signals to personalize your outreach.
                </p>
              </div>
            </div>

            <div className="flex gap-4">
              <div className="w-12 h-12 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-time-line text-xl text-indigo-600"></i>
              </div>
              <div>
                <h3 className="text-xl font-bold text-slate-900 mb-2">Sales Intelligence</h3>
                <p className="text-slate-600 leading-relaxed">
                  Receive real-time alerts on trigger events, company changes, and buying signals that indicate the perfect time to engage with prospects.
                </p>
              </div>
            </div>
          </div>

          <h2 className="text-3xl font-bold text-slate-900 mb-6">Benefits</h2>
          <ul className="space-y-3 mb-12">
            <li className="flex items-start gap-3">
              <i className="ri-check-line text-xl text-indigo-600 mt-1"></i>
              <span className="text-slate-600">Accelerate your sales cycle by focusing on qualified leads</span>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-check-line text-xl text-indigo-600 mt-1"></i>
              <span className="text-slate-600">Increase conversion rates with better lead qualification</span>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-check-line text-xl text-indigo-600 mt-1"></i>
              <span className="text-slate-600">Maximize sales team productivity and efficiency</span>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-check-line text-xl text-indigo-600 mt-1"></i>
              <span className="text-slate-600">Reduce customer acquisition costs with targeted outreach</span>
            </li>
            <li className="flex items-start gap-3">
              <i className="ri-check-line text-xl text-indigo-600 mt-1"></i>
              <span className="text-slate-600">Build a stronger, more predictable sales pipeline</span>
            </li>
          </ul>

          <div className="bg-slate-50 rounded-2xl p-8 mt-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4">Ready to Get Started?</h2>
            <p className="text-slate-600 mb-6">
              Transform your sales process with our lead enablement solutions and start closing more deals today.
            </p>
            <Link 
              to="/"
              onClick={(e) => {
                e.preventDefault();
                window.REACT_APP_NAVIGATE('/');
                setTimeout(() => {
                  const element = document.getElementById('contact');
                  if (element) {
                    const offset = 80;
                    const elementPosition = element.getBoundingClientRect().top;
                    const offsetPosition = elementPosition + window.pageYOffset - offset;
                    window.scrollTo({
                      top: offsetPosition,
                      behavior: 'smooth'
                    });
                  }
                }, 100);
              }}
              className="inline-flex items-center gap-2 bg-indigo-600 text-white px-6 py-3 rounded-lg font-semibold hover:bg-indigo-700 transition-colors whitespace-nowrap cursor-pointer"
            >
              Contact Us
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
